import React, { useState, useEffect } from 'react';
import ModalBase from './ModalBase';
import { SpecType as AddSpecType } from './AddSpecModal'; // Type from AddSpecModal for specType prop
import {
    SpecDetail,
    SpecDetailString,
    SpecDetailStringList,
    SpecDetailTable,
    SpecStringContent,
    SpecStringListContent,
    SpecTableContent,
    SpecType as FullSpecType,
} from '../types';
// import SpecTextEdit from './SpecTextEdit'; // No longer directly used
// import SpecTableEdit from './SpecTableEdit'; // No longer directly used
import SpecEditForm from './SpecEditForm'; // Import the new form component
// Import other necessary components and types

interface CreateSpecDetailModalProps {
    specType: AddSpecType;
    specName: string; // This is the internal value/key
    specLabel: string; // This is for display
    onClose: () => void; // Called on save success or explicit close (X icon)
    onSave: (newSpec: SpecDetail) => void;
    onSaveAndFinish?: (newSpec: SpecDetail) => void; // Optional callback for save and finish
    onBack: () => void; // Called when 'Back' button is clicked
    isFirstSpec?: boolean; // Whether this is the first spec being created
}

const CreateSpecDetailModal: React.FC<CreateSpecDetailModalProps> = ({
    specType,
    specName, // internal value
    specLabel, // display label
    onClose,
    onSave,
    onSaveAndFinish,
    onBack,
    isFirstSpec = false,
}) => {
    const [newSpec, setNewSpec] = useState<SpecDetail | null>(null);
    const [isModified, setIsModified] = useState(false);
    // Validation states needed for the save button, will be updated by SpecEditForm
    const [hasEmptyFields, setHasEmptyFields] = useState(true); // Assume empty initially
    const [hasTableError, setHasTableError] = useState(false);

    // Removed additionalInputs state as SpecEditForm will manage its own version internally
    // and consolidate before calling onSpecsChange.

    useEffect(() => {
        let initialSpecData: SpecDetail;
        if (specType === 'text') {
            initialSpecData = {
                name: specName,
                display_name: specLabel,
                spec_type: FullSpecType.SPEC_STRING,
                language: null,
                content: {
                    text: '',
                    text_size: null,
                } as SpecStringContent,
            } as SpecDetailString;
        } else {
            // specType === 'table'
            initialSpecData = {
                name: specName,
                display_name: specName,
                spec_type: FullSpecType.SPEC_TABLE,
                language: null,
                content: {
                    headers: ['', ''], // Initialize with two empty headers for a 2x2 table
                    rows: [
                        ['', ''], // First row with two empty cells
                        ['', ''], // Second row with two empty cells
                    ],
                } as SpecTableContent,
            } as SpecDetailTable;
        }
        setNewSpec(initialSpecData);

        // Initialize validation status based on the new spec
        if (initialSpecData.spec_type === FullSpecType.SPEC_STRING) {
            const content = initialSpecData.content as SpecStringContent;
            setHasEmptyFields(content.text.trim() === '');
        } else if (
            initialSpecData.spec_type === FullSpecType.SPEC_STRING_LIST
        ) {
            const content = initialSpecData.content as SpecStringListContent;
            setHasEmptyFields(
                content.text.length === 0 ||
                    (content.text.length === 1 &&
                        content.text[0].trim() === ''),
            );
        } else {
            // For table or other types, default to not empty, SpecEditForm will provide accurate status.
            setHasEmptyFields(false);
        }
        setHasTableError(false); // Assuming no table error initially for non-table or empty table specs
        setIsModified(false);
    }, [specType, specName]);

    const handleFormChange = (
        updatedSpecs: SpecDetail[],
        isFormModified: boolean,
        formHasEmptyFields: boolean,
        formHasTableError: boolean,
    ) => {
        if (updatedSpecs.length > 0) {
            // Only update if the new spec data is actually different.
            if (JSON.stringify(newSpec) !== JSON.stringify(updatedSpecs[0])) {
                setNewSpec(updatedSpecs[0]); // Keep the single spec being created
            }
        }

        // If the form child indicates it has been modified, update the parent's
        // isModified state to true. The parent's isModified state should only be
        // reset to false by explicit actions (e.g., initial load, successful save).
        if (isFormModified && !isModified) {
            setIsModified(true);
        }
        // NOTE: We no longer set parent's isModified to false if isFormModified is false here,
        // because isFormModified from the child reflects its state relative to its *current* initialSpecs.
        // The parent needs to retain the "modified since initial load/save" status.

        if (hasEmptyFields !== formHasEmptyFields) {
            setHasEmptyFields(formHasEmptyFields);
        }
        if (hasTableError !== formHasTableError) {
            setHasTableError(formHasTableError);
        }
    };
    console.log(
        'handleSave',
        newSpec,
        hasEmptyFields,
        hasTableError,
        isModified,
    );

    const handleSave = () => {
        if (newSpec && !hasEmptyFields && !hasTableError) {
            onSave(newSpec);
        }
    };

    const handleSaveAndFinish = () => {
        if (newSpec && !hasEmptyFields && !hasTableError && onSaveAndFinish) {
            onSaveAndFinish(newSpec);
        }
    };

    if (!newSpec) {
        return (
            <ModalBase onClose={onClose}>
                <p>Loading spec editor...</p>
            </ModalBase>
        ); // Or some loading indicator
    }

    const canSave = isModified && !hasEmptyFields && !hasTableError;

    return (
        <ModalBase
            onClose={onClose}
            width={specType === 'text' ? '800px' : '1000px'}
        >
            <div className="spec-edit-modal-content">
                {/* Reusing existing styling */}
                <h2>
                    {isFirstSpec ? 'Create First' : 'Create New'} {specLabel}
                </h2>
                <div className="spec-edit-form">
                    {/* Reusing existing styling */}
                    {/* Use SpecEditForm for rendering and editing the single new spec */}
                    {newSpec && (
                        <SpecEditForm
                            initialSpecs={[newSpec]} // Pass the single spec as an array
                            onSpecsChange={handleFormChange}
                            isCreatingNewSpec={true} // Indicate this is for creating a new spec
                        />
                    )}
                </div>
                <div className="button-row">
                    <button className="btn-secondary" onClick={onBack}>
                        Back
                    </button>
                    {onSaveAndFinish && (
                        <button
                            className="btn-secondary"
                            onClick={handleSaveAndFinish}
                            disabled={!canSave}
                        >
                            Save & Finish
                        </button>
                    )}
                    <button
                        className="btn-primary"
                        onClick={handleSave}
                        disabled={!canSave}
                    >
                        {isFirstSpec ? 'Save & Add Next' : 'Save & Add Another'}
                    </button>
                </div>
            </div>
        </ModalBase>
    );
};

export default CreateSpecDetailModal;
